import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  PhoneForwarded,
  UserCheck,
  Building,
  PhoneCall,
  Zap,
  AlertCircle,
  MessageSquare,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';

export interface CallTransferNodeData {
  transferPhoneNumber?: string;
  departmentName?: string;
  transferVoicePrompt?: string;
  alertAgentViaWhatsApp?: boolean;
  isCustomizable?: boolean;
  transferCount?: number;
  [key: string]: unknown;
}

export const CallTransferNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as CallTransferNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof CallTransferNodeData, value: any, allUpdates?: Partial<CallTransferNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'CALL_TRANSFER',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[CallTransferNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof CallTransferNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const alertAgentViaWhatsApp = nodeData.alertAgentViaWhatsApp !== false;
  const transferCount = typeof nodeData.transferCount === 'number' ? nodeData.transferCount : 0;

  return (
    <div
      className={`group relative rounded-2xl border transition-all duration-300 backdrop-blur-xl min-w-[320px] max-w-[360px] bg-gradient-to-b from-[#0F0E24]/98 via-[#090817]/98 to-[#05040D]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-indigo-400 ring-2 ring-indigo-400/50 shadow-[0_0_35px_rgba(99,102,241,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-indigo-500/40 hover:border-indigo-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('call_transfer', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-950 via-slate-900 to-emerald-950 border-2 border-indigo-400 text-indigo-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(99,102,241,0.8)] hover:border-indigo-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab"
          >
            <Zap size={13} className="text-indigo-300 animate-pulse" />
            <span>Escalation Routing Studio</span>
            <ArrowRight size={13} className="text-indigo-400" />
          </button>
        </div>
      )}
      {/* ── Connection Handles (4-Way Topology) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-transfer"
        className="!w-4 !h-4 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 z-20 shadow-[0_0_12px_rgba(16,185,129,1)]"
        title="Input: Connect from Luna Core Hub (out-whatsapp / Live Calls)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-transfer"
        className="!w-4 !h-4 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 z-20 shadow-[0_0_12px_rgba(16,185,129,1)]"
        title="Output: Forward to subsequent action"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!w-3.5 !h-3.5 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!w-3.5 !h-3.5 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
      />

      {/* Header */}
      <div className="flex items-center justify-between p-3.5 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-950/80 via-purple-950/50 to-slate-950/80 rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 shadow-[0_0_12px_rgba(99,102,241,0.3)]">
            <PhoneForwarded className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="text-xs font-bold text-white tracking-wide uppercase">Call Transfer</h4>
              {isWired && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  <Zap className="w-2.5 h-2.5 mr-0.5 animate-pulse text-indigo-400" />
                  Live
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400">Human Agent & Dept Forwarding</p>
          </div>
        </div>

        {/* Master Admin / Tenant Governance */}
        <div className="flex items-center gap-1">
          {saveIndicator && (
            <span className="text-[9px] font-medium text-emerald-400 bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/30 animate-pulse">
              {saveIndicator}
            </span>
          )}
        </div>
      </div>

      {/* Live Badge Telemetry */}
      {transferCount > 0 && (
        <div className="mx-3.5 mt-2 px-2 py-1 rounded-lg bg-indigo-950/60 border border-indigo-500/30 flex items-center justify-between text-[10px] text-indigo-300">
          <span className="flex items-center gap-1">
            <PhoneCall className="w-3 h-3 text-indigo-400" /> Total Escalations:
          </span>
          <span className="font-bold text-white">{transferCount} transferred</span>
        </div>
      )}

      {/* Node Content */}
      <div className="p-3.5 space-y-3 text-xs">
        {/* Human Agent Phone Number */}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
            Agent Phone Number (Forwarding Target)
          </label>
          <div className="relative">
            <input
              type="text"
              value={nodeData.transferPhoneNumber || ''}
              onChange={(e) => handleChange('transferPhoneNumber', e.target.value)}
              placeholder="e.g. +94771234567"
              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Department or Representative Name */}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
            Department / Representative Name
          </label>
          <input
            type="text"
            value={nodeData.departmentName || ''}
            onChange={(e) => handleChange('departmentName', e.target.value)}
            placeholder="e.g. Senior Sales Manager / Support Desk"
            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Voice Announcement Prompt */}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
            Transfer Announcement Prompt
          </label>
          <textarea
            rows={2}
            value={nodeData.transferVoicePrompt || ''}
            onChange={(e) => handleChange('transferVoicePrompt', e.target.value)}
            placeholder="e.g. මම ඔබගේ ඇමතුම අපගේ ප්‍රධාන කළමනාකරු වෙත යොමු කරනවා, මඳක් රැඳී සිටින්න."
            className="w-full bg-slate-950/50 border border-slate-700/80 rounded-lg p-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 resize-none"
          />
        </div>

        {/* WhatsApp Alert to Agent Toggle */}
        <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/40 border border-slate-800">
          <div className="flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-[10px] text-slate-300">WhatsApp Alert to Agent</span>
          </div>
          <input
            type="checkbox"
            checked={alertAgentViaWhatsApp}
            onChange={(e) => handleChange('alertAgentViaWhatsApp', e.target.checked)}
            className="w-3.5 h-3.5 accent-indigo-500 cursor-pointer rounded"
          />
        </div>
      </div>
    </div>
  );
});

CallTransferNode.displayName = 'CallTransferNode';
export default CallTransferNode;
